import i18n from '../../../i18n';
import { objectKeysToLowerCaseKeys, getUniqueThemes } from '../../../services/utilities';
import { getLocationStringFromCoordinates } from '../../../services/feedback';
import { prepareLayer } from '../../../services/map';
import {
    NG_LAYER_TYPES,
    SUPPORTED_IMPORT_GEOMETRY_TYPES,
    CONTENT_TYPE_ID
} from '../../../constants';


export const actions = {
    async loadFeedbacks({ dispatch, commit, state }, force) {
        if (!force && state.feedbacks.length > 0) {
            return state.feedbacks;
        }
        let loadedFeedbacks = await dispatch('useApiData', { name: 'GetFeedbackTypeOverviewMobile' });

        loadedFeedbacks = Object.assign([], getUniqueThemes(loadedFeedbacks?.data));

        // Filter out the web personal measures
        loadedFeedbacks = loadedFeedbacks.filter(feedback => !(feedback.feedbackType.contentTypeId === CONTENT_TYPE_ID.MEASUREMENT
            && feedback.feedbackType.personal && feedback.feedbackType.useInWebTheme));

        loadedFeedbacks?.forEach(feedbackData => commit('ADD_FEEDBACK', { feedbackData, active: false }));

        // Show/hide feedback button depending on availability of feedbacks
        // feedback must be user editable and of type feedback
        // and feedback should not be a webfeedback
        const feedbacksEnabled = loadedFeedbacks.some(feedback => feedback.userEdit &&
            !feedback.feedbackType.useInWebTheme &&
            (feedback.feedbackType.contentTypeId === CONTENT_TYPE_ID.FEEDBACK));
        commit('TOGGLE_NAVBAR_FEEBACK_BUTTON', feedbacksEnabled);

        // Show/hide measurement button depending on availability of measurements
        // feedback must be user editable and of type measurement
        const measurementsEnabled = loadedFeedbacks.some(feedback => feedback.userEdit && (feedback.feedbackType.contentTypeId === CONTENT_TYPE_ID.MEASUREMENT));
        commit('TOGGLE_NAVBAR_MEASUREMENT_BUTTON', measurementsEnabled);

        return loadedFeedbacks;
    },

    async toggleFeedback({ dispatch, getters, commit }, { mapName, feedbackId, active }) {
        try {
            if (active) {
                const loadedFeedback = await dispatch('loadFeedback', { feedbackId });
                loadedFeedback?.themeLayers.map(layer => dispatch('toggleFeedbackLayer', { mapName, id: layer.id, active }));
            } else {
                const feedback = getters.getFeedbackById(feedbackId);
                const layerId = feedback.layerId;

                dispatch('toggleFeedbackLayer', { mapName, id: layerId, active });

                // Remove current selection for this layer
                if (feedback?.data?.name) {
                    commit('CLEAR_SELECTION_DATA', { title: feedback.data.name });
                }
            }
        } catch (err) {
            throw new Error('Feedbacklayer feedback not found');
        }
    },

    async toggleAllFeedbacks({ dispatch, state }, { mapName, active, contentTypeId }) {
        state.feedbacks
            .filter(feedback => feedback.layerId !== null && feedback.data.feedbackType.contentTypeId === contentTypeId)
            .map(feedback => dispatch('toggleFeedbackLayer', { mapName, id: feedback.layerId, active }));
    },

    async loadFeedback({ dispatch, commit, getters }, { feedbackId, force }) {
        if (!force) {
            // do not load data that is allready loaded
            // check if layerId has been set (by CONNECT_LAYER_TO_FEEDBACK)
            const currentFeedback = getters.getFeedbackById(feedbackId);
            if (currentFeedback?.layerId > 0) {
                return currentFeedback.data;
            }
        }
        const feedbackDataResult = await dispatch('useApiData', {
            name: 'MobileGetTheme',
            parameters: { id: feedbackId }
        });
        const { geometryType, contentTypeId } = feedbackDataResult.data.feedbackType;
        await Promise.all(feedbackDataResult.data.themeLayers.map(_layerData => dispatch('addLayer', {
            type: 'feedback',
            layerData: { ..._layerData, feedbackId, contentTypeId, geometryType },
            active: false
        })));
        commit('CONNECT_LAYER_TO_FEEDBACK', { id: feedbackId, data: feedbackDataResult.data });
        return feedbackDataResult.data;
    },

    async toggleFeedbackLayer({ dispatch, state, commit }, { mapName, id, active }) {
        if (active) {
            try {
                await dispatch('addLayerToMap', { mapName, id });
            } catch (err) {
                if (err.message === 'layer not found') {
                    dispatch('setLayerErrorState', { layerId: id, error: err });
                }
            }

        } else {
            await dispatch('removeLayerFromMap', { mapName, id });
        }
    },

    async addImportFeedback(store, { importData, feedbackId }) {
        let feedback = store.getters.getFeedbackById(feedbackId);
        if (!feedback) {
            return;
        }
        const olLayer = await prepareLayer(store, null, {
            type: NG_LAYER_TYPES.CUSTOM,
            data: importData,
            extended: importData
        });
        const features = olLayer.getSource().getFeatures();
        if (!features?.length > 0) {
            return;
        }

        await store.dispatch('loadFeedback', { feedbackId });
        feedback = store.getters.getFeedbackById(feedbackId);
        const { id } = store.getters.getLayerById(
            feedback.layerId
        );

        const importFormats = features.map((feature) => {
            return feature.getGeometry().getType();
        });
        const uniqueImportFormats = [...new Set(importFormats)];
        const notSupported = uniqueImportFormats.filter(format => !SUPPORTED_IMPORT_GEOMETRY_TYPES.includes(format));
        if (notSupported.length > 0) {
            const wantsToContinue = await store.dispatch('askConfirm', {
                message: 'importLayer.invalidGeometryTypesConfirmation',
                messageVars: [notSupported.join(', '), SUPPORTED_IMPORT_GEOMETRY_TYPES.join(', ')],
                options: {
                    okTitle: i18n.t('btnTitleYes'),
                    cancelTitle: i18n.t('btnTitleNo')
                }
            });
            if (!wantsToContinue) {
                return;
            }
        }

        const createPolygonString = (polygon) => {
            const coords = polygon.getCoordinates();
            const hasHoles = polygon.getLinearRingCount() > 1;
            if (hasHoles) {
                const rings = coords.map(ring => {
                    return `(${getLocationStringFromCoordinates([ring], 'Polygon', undefined, true)})`;
                });
                return rings.join(',');
            }
            return `(${getLocationStringFromCoordinates(coords, 'Polygon', undefined, true)})`;
        };

        const data = features
            .filter(feature => {
                const geometryType = feature.getGeometry().getType();
                return SUPPORTED_IMPORT_GEOMETRY_TYPES.find(supportedFormat => supportedFormat === geometryType);
            })
            .map((feature) => {
                const geometryType = feature.getGeometry().getType();
                const geom = feature.getGeometry();

                let geolocation;
                if (geometryType === 'Polygon') {
                    geolocation = createPolygonString(geom);
                } else if (geometryType === 'MultiPolygon') {
                    const multiPolygonStrings = [...geom.getPolygons()].map(polygon => {
                        return `(${createPolygonString(polygon)})`;
                    });
                    geolocation = `${multiPolygonStrings.toString()}`;
                } else {
                    geolocation = getLocationStringFromCoordinates(geom.getCoordinates(), geometryType, undefined, true);
                }
                const properties = objectKeysToLowerCaseKeys(feature.getProperties());
                return {
                    ...properties,
                    gpslocation: null,
                    geometry: undefined,
                    geolocation,
                    geometryType
                };
            });

        try {
            await store.dispatch('useApiData', {
                name: 'ImportGeoJson',
                parameters: id,
                useLoader: true,
                data
            });
        } catch (error) {
            store.commit('ADD_STATUS_MESSAGE', {
                message: i18n.t('importLayer.importFailMessage'),
                variant: 'danger',
                autoHideDelay: 5000
            });
        }
    }
};
